import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Editor } from '@tinymce/tinymce-react';

const OrdenCompraTemplateEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [empresa, setEmpresa] = useState(null);
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const apiKey = process.env.REACT_APP_TINYMCE_API_KEY || 'YOUR_TINYMCE_API_KEY';

    useEffect(() => {
        // Fetch the company name for the header
        axios.get(`/api/empresas/${id}`)
            .then(response => setEmpresa(response.data))
            .catch(err => {
                setError('Could not fetch company details.');
                console.error(err);
            });

        // Fetch the template content directly from the dedicated endpoint
        axios.get(`/api/empresas/${id}/template/oc`)
            .then(response => {
                // The response is now JSON, so we access the 'template' property
                setContent(response.data.template);
                setLoading(false);
            })
            .catch(err => {
                setError('Could not fetch the OC template.');
                setLoading(false);
                console.error(err);
            });
    }, [id]);

    const handleSave = () => {
        // No need to use the ref anymore, the content state is the source of truth
        axios.put(`/api/empresas/${id}/template/oc`, content, {
            headers: { 'Content-Type': 'text/html' }
        })
        .then(() => {
            alert('Template saved successfully!');
            navigate(`/empresas/${id}`);
        })
        .catch(err => {
            setError('Error saving template.');
            console.error(err);
        });
    };

    if (loading) return <p>Loading template...</p>;
    if (error) return <p style={{ color: 'red' }}>{error}</p>;

    return (
        <div style={{ padding: '20px' }}>
            <h2>Edit Purchase Order Template for: {empresa ? empresa.nombre : '...'}</h2>
            <p>Use the placeholders below to customize your template. The data will be filled in automatically when a new Purchase Order is created.</p>
            
            <div style={{ display: 'flex', gap: '20px' }}>
                <div style={{ flex: 3 }}>
                    {/* The Editor is now a controlled component */}
                    <Editor
                        apiKey={apiKey}
                        value={content}
                        onEditorChange={(newContent, editor) => setContent(newContent)}
                        init={{
                            height: 600,
                            menubar: true,
                            plugins: [
                                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                                'preview', 'anchor', 'searchreplace', 'visualblocks', 'code',
                                'fullscreen', 'insertdatetime', 'media', 'table', 'help', 'wordcount'
                            ],
                            toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | table | code | help',
                            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                        }}
                    />
                </div>
                <div style={{ flex: 1, border: '1px solid #ccc', padding: '10px', backgroundColor: '#f9f9f9' }}>
                    <h3>Available Placeholders</h3>
                    <p>Copy and paste these into the editor.</p>
                    <strong>Empresa:</strong>
                    <ul>
                        <li>{`{{ empresa.nombre }}`}</li>
                        <li>{`{{ empresa.rfc }}`}</li>
                        <li>{`{{ empresa.direccion_fiscal }}`}</li>
                        <li>{`{{ empresa.logo_url }}`}</li>
                    </ul>
                    <strong>Proveedor:</strong>
                    <ul>
                        <li>{`{{ proveedor.nombre }}`}</li>
                        <li>{`{{ proveedor.nombre_comercial }}`}</li>
                        <li>{`{{ proveedor.rfc }}`}</li>
                        <li>{`{{ proveedor.correo_electronico }}`}</li>
                        <li>{`{{ proveedor.telefono }}`}</li>
                        <li>{`{{ proveedor.banco }}`}</li>
                        <li>{`{{ proveedor.beneficiario }}`}</li>
                        <li>{`{{ proveedor.numero_cuenta }}`}</li>
                        <li>{`{{ proveedor.clabe }}`}</li>
                    </ul>
                    <strong>Orden de Compra:</strong>
                    <ul>
                        <li>{`{{ oc.id }}`}</li>
                        <li>{`{{ oc.created_at }}`}</li>
                        <li>{`{{ oc.requisicion_id }}`}</li>
                        <li>{`{{ oc.subtotal }}`}</li>
                        <li>{`{{ oc.iva }}`}</li>
                        <li>{`{{ oc.total }}`}</li>
                        <li>{`{{ oc.moneda }}`}</li>
                        <li>{`{{ oc.metodo_pago }}`}</li>
                        <li>{`{{ oc.fecha_limite_pago }}`}</li>
                        <li>{`{{ oc.condiciones_pago }}`}</li>
                    </ul>
                    <strong>Items Table:</strong>
                    <pre>
{`<table>
  <thead>
    <tr>
      <th>Description</th>
      <th>Quantity</th>
    </tr>
  </thead>
  <tbody>
    {% for item in detalles %}
    <tr>
      <td>{{ item.descripcion }}</td>
      <td>{{ item.cantidad }}</td>
    </tr>
    {% endfor %}
  </tbody>
</table>`}
                    </pre>
                </div>
            </div>

            <button onClick={handleSave} style={{ marginTop: '10px', padding: '10px 20px' }}>
                Save Template
            </button>
            <button onClick={() => navigate(`/empresas/${id}`)} style={{ marginLeft: '10px' }}>
                Cancel
            </button>
        </div>
    );
};

export default OrdenCompraTemplateEditor;
